import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const SitemapPage = lazy(() => import('../pages/sitemap/page'));
const ResidentialElectricalPage = lazy(() => import('../pages/services/residential-electrical/page'));
const TestingCertificationPage = lazy(() => import('../pages/services/testing-certification/page'));
const RepairsMaintenancePage = lazy(() => import('../pages/services/repairs-maintenance/page'));
const LightingOutdoorPage = lazy(() => import('../pages/services/lighting-outdoor/page'));
const CommercialSpecialistPage = lazy(() => import('../pages/services/commercial-specialist/page'));
const SmartHomeEnergyPage = lazy(() => import('../pages/services/smart-home-energy/page'));
const RenewablesEVPage = lazy(() => import('../pages/services/renewables-ev/page'));
const FireSafetyPage = lazy(() => import('../pages/services/fire-safety/page'));

// Residential Electrical Sub-Services
const FullHouseRewiringPage = lazy(() => import('../pages/services/residential-electrical/full-house-rewiring/page'));
const PartialRewiringPage = lazy(() => import('../pages/services/residential-electrical/partial-rewiring/page'));
const PanelUpgradesPage = lazy(() => import('../pages/services/residential-electrical/panel-upgrades/page'));
const NewCircuitsPage = lazy(() => import('../pages/services/residential-electrical/new-circuits/page'));
const KitchenElectricalPage = lazy(() => import('../pages/services/residential-electrical/kitchen-electrical/page'));
const BathroomElectricalPage = lazy(() => import('../pages/services/residential-electrical/bathroom-electrical/page'));
const ApplianceCircuitsPage = lazy(() => import('../pages/services/residential-electrical/appliance-circuits/page'));
const CeilingFanInstallationPage = lazy(() => import('../pages/services/residential-electrical/ceiling-fan-installation/page'));
const SmokeDetectorInstallationPage = lazy(() => import('../pages/services/residential-electrical/smoke-detector-installation/page'));
const CarbonMonoxideDetectorPage = lazy(() => import('../pages/services/residential-electrical/carbon-monoxide-detector-installation/page'));
const HomeSurgeProtectionPage = lazy(() => import('../pages/services/residential-electrical/home-surge-protection/page'));
const HomeRenovationElectricalPage = lazy(() => import('../pages/services/residential-electrical/home-renovation-electrical/page'));
const NewHomeBuildElectricalPage = lazy(() => import('../pages/services/residential-electrical/new-home-electrical/page'));
const GarageElectricalPage = lazy(() => import('../pages/services/residential-electrical/garage-electrical/page'));
const HomeElectricalInspectionPage = lazy(() => import('../pages/services/residential-electrical/home-electrical-inspection/page'));

// Testing & Certification Sub-Services
const ResidentialElectricalSafetyInspectionsPage = lazy(() => import('../pages/services/testing-certification/residential-electrical-safety-inspections/page'));
const PrePurchaseElectricalInspectionsPage = lazy(() => import('../pages/services/testing-certification/pre-purchase-electrical-inspections/page'));
const LandlordElectricalSafetyChecksPage = lazy(() => import('../pages/services/testing-certification/landlord-electrical-safety-checks/page'));
const CodeComplianceInspectionsPage = lazy(() => import('../pages/services/testing-certification/code-compliance-inspections/page'));
const InsuranceElectricalInspectionsPage = lazy(() => import('../pages/services/testing-certification/insurance-electrical-inspections/page'));
const ElectricalFaultDiagnosticsPage = lazy(() => import('../pages/services/testing-certification/electrical-fault-diagnostics/page'));
const CircuitLoadTestingPage = lazy(() => import('../pages/services/testing-certification/circuit-load-testing/page'));
const BreakerPanelTestingPage = lazy(() => import('../pages/services/testing-certification/breaker-panel-testing/page'));
const WiringConditionInspectionsPage = lazy(() => import('../pages/services/testing-certification/wiring-condition-inspections/page'));
const ThermalImagingElectricalInspectionsPage = lazy(() => import('../pages/services/testing-certification/thermal-imaging-electrical-inspections/page'));

// Repairs & Maintenance Sub-Services
const EmergencyElectricalRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/emergency-electrical-repairs/page'));
const ElectricalFaultFindingPage = lazy(() => import('../pages/services/repairs-maintenance/electrical-fault-finding/page'));
const TrippingBreakerRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/tripping-breaker-repairs/page'));
const DeadOutletRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/dead-outlet-repairs/page'));
const FlickeringLightRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/flickering-light-repairs/page'));
const BurningSmellInvestigationPage = lazy(() => import('../pages/services/repairs-maintenance/burning-smell-investigation/page'));
const ElectricalWiringRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/electrical-wiring-repairs/page'));
const CircuitBreakerRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/circuit-breaker-repairs/page'));
const DamagedOutletSwitchRepairsPage = lazy(() => import('../pages/services/repairs-maintenance/damaged-outlet-switch-repairs/page'));
const ElectricalMaintenancePlansPage = lazy(() => import('../pages/services/repairs-maintenance/electrical-maintenance-plans/page'));

// Lighting & Outdoor Sub-Services
const InteriorLightingInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/interior-lighting-installation/page'));
const RecessedDownlightInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/recessed-downlight-installation/page'));
const LEDLightingUpgradesPage = lazy(() => import('../pages/services/lighting-outdoor/led-lighting-upgrades/page'));
const KitchenUnderCabinetLightingPage = lazy(() => import('../pages/services/lighting-outdoor/kitchen-under-cabinet-lighting/page'));
const PendantLightingInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/pendant-lighting-installation/page'));
const SecurityLightingInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/security-lighting-installation/page'));
const MotionSensorLightingPage = lazy(() => import('../pages/services/lighting-outdoor/motion-sensor-lighting/page'));
const LandscapeLightingInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/landscape-lighting-installation/page'));
const GardenPathwayLightingPage = lazy(() => import('../pages/services/lighting-outdoor/garden-pathway-lighting/page'));
const OutdoorPowerOutletInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/outdoor-power-outlet-installation/page'));
const PatioDeckLightingPage = lazy(() => import('../pages/services/lighting-outdoor/patio-deck-lighting/page'));
const ParkingLotLightingInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/parking-lot-lighting-installation/page'));
const FloodlightInstallationPage = lazy(() => import('../pages/services/lighting-outdoor/floodlight-installation/page'));

// Commercial Specialist Sub-Services
const CommercialElectricalInstallationsPage = lazy(() => import('../pages/services/commercial-specialist/commercial-electrical-installations/page'));
const CommercialElectricalMaintenancePage = lazy(() => import('../pages/services/commercial-specialist/commercial-electrical-maintenance/page'));
const OfficeElectricalFitOutsPage = lazy(() => import('../pages/services/commercial-specialist/office-electrical-fit-outs/page'));
const RetailStoreElectricalPage = lazy(() => import('../pages/services/commercial-specialist/retail-store-electrical/page'));
const RestaurantElectricalSystemsPage = lazy(() => import('../pages/services/commercial-specialist/restaurant-electrical-systems/page'));
const WarehouseElectricalPage = lazy(() => import('../pages/services/commercial-specialist/warehouse-electrical/page'));
const ThreePhasePowerPage = lazy(() => import('../pages/services/commercial-specialist/three-phase-power/page'));
const CommercialLightingSystemsPage = lazy(() => import('../pages/services/commercial-specialist/commercial-lighting-systems/page'));
const EmergencyLightingPage = lazy(() => import('../pages/services/commercial-specialist/emergency-lighting/page'));
const CommercialPanelUpgradesPage = lazy(() => import('../pages/services/commercial-specialist/commercial-panel-upgrades/page'));
const NewCommercialBuildsPage = lazy(() => import('../pages/services/commercial-specialist/new-commercial-builds/page'));
const PropertyDevelopersPage = lazy(() => import('../pages/services/commercial-specialist/property-developers/page'));

// Smart Home & Energy Sub-Services
const SmartLightingInstallationPage = lazy(() => import('../pages/services/smart-home-energy/smart-lighting-installation/page'));
const SmartSwitchInstallationPage = lazy(() => import('../pages/services/smart-home-energy/smart-switch-installation/page'));
const SmartThermostatInstallationPage = lazy(() => import('../pages/services/smart-home-energy/smart-thermostat-installation/page'));
const HomeAutomationSystemsPage = lazy(() => import('../pages/services/smart-home-energy/home-automation-systems/page'));
const SmartSecuritySystemsPage = lazy(() => import('../pages/services/smart-home-energy/smart-security-systems/page'));
const EnergyMonitoringSystemsPage = lazy(() => import('../pages/services/smart-home-energy/energy-monitoring-systems/page'));
const WholeHomeEnergyManagementPage = lazy(() => import('../pages/services/smart-home-energy/whole-home-energy-management/page'));
const SmartDoorbellInstallationPage = lazy(() => import('../pages/services/smart-home-energy/smart-doorbell-installation/page'));
const SmartApplianceIntegrationPage = lazy(() => import('../pages/services/smart-home-energy/smart-appliance-integration/page'));
const SmartHomeSetupConfigurationPage = lazy(() => import('../pages/services/smart-home-energy/smart-home-setup-configuration/page'));

// Renewables & EV Sub-Services
const EVChargerInstallationPage = lazy(() => import('../pages/services/renewables-ev/ev-charger-installation/page'));
const TeslaChargerInstallationPage = lazy(() => import('../pages/services/renewables-ev/tesla-charger-installation/page'));
const HomeEVChargingStationsPage = lazy(() => import('../pages/services/renewables-ev/home-ev-charging-stations/page'));
const CommercialEVChargerInstallationsPage = lazy(() => import('../pages/services/renewables-ev/commercial-ev-charger-installations/page'));
const SolarPanelElectricalConnectionsPage = lazy(() => import('../pages/services/renewables-ev/solar-panel-electrical-connections/page'));
const SolarInverterInstallationPage = lazy(() => import('../pages/services/renewables-ev/solar-inverter-installation/page'));
const SolarBatteryStorageInstallationPage = lazy(() => import('../pages/services/renewables-ev/solar-battery-storage-installation/page'));
const BackupBatterySystemsPage = lazy(() => import('../pages/services/renewables-ev/backup-battery-systems/page'));
const ElectricalSystemsSolarInstallationsPage = lazy(() => import('../pages/services/renewables-ev/electrical-systems-solar-installations/page'));
const SolarSystemMonitoringSetupPage = lazy(() => import('../pages/services/renewables-ev/solar-system-monitoring-setup/page'));

// Fire Safety Sub-Services
const FireAlarmInstallationPage = lazy(() => import('../pages/services/fire-safety/fire-alarm-installation/page'));
const FireAlarmMaintenancePage = lazy(() => import('../pages/services/fire-safety/fire-alarm-maintenance/page'));
const SmokeDetectorSystemsPage = lazy(() => import('../pages/services/fire-safety/smoke-detector-systems/page'));
const CarbonMonoxideDetectionPage = lazy(() => import('../pages/services/fire-safety/carbon-monoxide-detection/page'));
const EmergencyLightingSystemsPage = lazy(() => import('../pages/services/fire-safety/emergency-lighting-systems/page'));
const FireAlarmPanelInstallationPage = lazy(() => import('../pages/services/fire-safety/fire-alarm-panel-installation/page'));
const FireAlarmTestingCertificationPage = lazy(() => import('../pages/services/fire-safety/fire-alarm-testing-certification/page'));
const FireAlarmUpgradesPage = lazy(() => import('../pages/services/fire-safety/fire-alarm-upgrades/page'));
const FireAlarmMonitoringPage = lazy(() => import('../pages/services/fire-safety/fire-alarm-monitoring/page'));
const FireSafetyCompliancePage = lazy(() => import('../pages/services/fire-safety/fire-safety-compliance/page'));

const NotFound = lazy(() => import('../pages/NotFound'));

// Blog Pages
const BlogPage = lazy(() => import('../pages/blog/page'));
const PanelUpgradeBlogPost = lazy(() => import('../pages/blog/when-to-upgrade-electrical-panel-fresno/page'));
const EVChargerBlogPost = lazy(() => import('../pages/blog/ev-charger-installation-fresno-homeowners/page'));
const SafetyChecklistBlogPost = lazy(() => import('../pages/blog/fresno-home-electrical-safety-checklist/page'));
const LandlordBlogPost = lazy(() => import('../pages/blog/landlord-electrical-safety-checks-fresno/page'));
const SmartHomeBlogPost = lazy(() => import('../pages/blog/smart-home-upgrades-fresno-summer/page'));
const SolarBlogPost = lazy(() => import('../pages/blog/solar-electrical-connections-fresno/page'));

// Area Pages
const FresnoAreaRedirect = lazy(() => import('../pages/areas/fresno/page'));
const ClovisPage = lazy(() => import('../pages/areas/clovis/page'));
const MaderaPage = lazy(() => import('../pages/areas/madera/page'));
const VisaliaPage = lazy(() => import('../pages/areas/visalia/page'));
const HanfordPage = lazy(() => import('../pages/areas/hanford/page'));
const TularePage = lazy(() => import('../pages/areas/tulare/page'));
const SelmaPage = lazy(() => import('../pages/areas/selma/page'));
const SangerPage = lazy(() => import('../pages/areas/sanger/page'));
const ReedleyPage = lazy(() => import('../pages/areas/reedley/page'));
const DinubaPage = lazy(() => import('../pages/areas/dinuba/page'));
const PortervillePage = lazy(() => import('../pages/areas/porterville/page'));
const FowlerPage = lazy(() => import('../pages/areas/fowler/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/sitemap',
    element: <SitemapPage />,
  },
  // Residential Electrical
  {
    path: '/services/residential-electrical',
    element: <ResidentialElectricalPage />,
  },
  {
    path: '/services/residential-electrical/full-house-rewiring',
    element: <FullHouseRewiringPage />,
  },
  {
    path: '/services/residential-electrical/partial-rewiring',
    element: <PartialRewiringPage />,
  },
  {
    path: '/services/residential-electrical/panel-upgrades',
    element: <PanelUpgradesPage />,
  },
  {
    path: '/services/residential-electrical/new-circuits',
    element: <NewCircuitsPage />,
  },
  {
    path: '/services/residential-electrical/kitchen-electrical',
    element: <KitchenElectricalPage />,
  },
  {
    path: '/services/residential-electrical/bathroom-electrical',
    element: <BathroomElectricalPage />,
  },
  {
    path: '/services/residential-electrical/appliance-circuits',
    element: <ApplianceCircuitsPage />,
  },
  {
    path: '/services/residential-electrical/ceiling-fan-installation',
    element: <CeilingFanInstallationPage />,
  },
  {
    path: '/services/residential-electrical/smoke-detector-installation',
    element: <SmokeDetectorInstallationPage />,
  },
  {
    path: '/services/residential-electrical/carbon-monoxide-detector-installation',
    element: <CarbonMonoxideDetectorPage />,
  },
  {
    path: '/services/residential-electrical/home-surge-protection',
    element: <HomeSurgeProtectionPage />,
  },
  {
    path: '/services/residential-electrical/home-renovation-electrical',
    element: <HomeRenovationElectricalPage />,
  },
  {
    path: '/services/residential-electrical/new-home-electrical',
    element: <NewHomeBuildElectricalPage />,
  },
  {
    path: '/services/residential-electrical/garage-electrical',
    element: <GarageElectricalPage />,
  },
  {
    path: '/services/residential-electrical/home-electrical-inspection',
    element: <HomeElectricalInspectionPage />,
  },
  // Testing & Certification
  {
    path: '/services/testing-certification',
    element: <TestingCertificationPage />,
  },
  {
    path: '/services/testing-certification/residential-electrical-safety-inspections',
    element: <ResidentialElectricalSafetyInspectionsPage />,
  },
  {
    path: '/services/testing-certification/pre-purchase-electrical-inspections',
    element: <PrePurchaseElectricalInspectionsPage />,
  },
  {
    path: '/services/testing-certification/landlord-electrical-safety-checks',
    element: <LandlordElectricalSafetyChecksPage />,
  },
  {
    path: '/services/testing-certification/code-compliance-inspections',
    element: <CodeComplianceInspectionsPage />,
  },
  {
    path: '/services/testing-certification/insurance-electrical-inspections',
    element: <InsuranceElectricalInspectionsPage />,
  },
  {
    path: '/services/testing-certification/electrical-fault-diagnostics',
    element: <ElectricalFaultDiagnosticsPage />,
  },
  {
    path: '/services/testing-certification/circuit-load-testing',
    element: <CircuitLoadTestingPage />,
  },
  {
    path: '/services/testing-certification/breaker-panel-testing',
    element: <BreakerPanelTestingPage />,
  },
  {
    path: '/services/testing-certification/wiring-condition-inspections',
    element: <WiringConditionInspectionsPage />,
  },
  {
    path: '/services/testing-certification/thermal-imaging-electrical-inspections',
    element: <ThermalImagingElectricalInspectionsPage />,
  },
  // Repairs & Maintenance
  {
    path: '/services/repairs-maintenance',
    element: <RepairsMaintenancePage />,
  },
  {
    path: '/services/repairs-maintenance/emergency-electrical-repairs',
    element: <EmergencyElectricalRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/electrical-fault-finding',
    element: <ElectricalFaultFindingPage />,
  },
  {
    path: '/services/repairs-maintenance/tripping-breaker-repairs',
    element: <TrippingBreakerRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/dead-outlet-repairs',
    element: <DeadOutletRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/flickering-light-repairs',
    element: <FlickeringLightRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/burning-smell-investigation',
    element: <BurningSmellInvestigationPage />,
  },
  {
    path: '/services/repairs-maintenance/electrical-wiring-repairs',
    element: <ElectricalWiringRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/circuit-breaker-repairs',
    element: <CircuitBreakerRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/damaged-outlet-switch-repairs',
    element: <DamagedOutletSwitchRepairsPage />,
  },
  {
    path: '/services/repairs-maintenance/electrical-maintenance-plans',
    element: <ElectricalMaintenancePlansPage />,
  },
  // Lighting & Outdoor
  {
    path: '/services/lighting-outdoor',
    element: <LightingOutdoorPage />,
  },
  {
    path: '/services/lighting-outdoor/interior-lighting-installation',
    element: <InteriorLightingInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/recessed-downlight-installation',
    element: <RecessedDownlightInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/led-lighting-upgrades',
    element: <LEDLightingUpgradesPage />,
  },
  {
    path: '/services/lighting-outdoor/kitchen-under-cabinet-lighting',
    element: <KitchenUnderCabinetLightingPage />,
  },
  {
    path: '/services/lighting-outdoor/pendant-lighting-installation',
    element: <PendantLightingInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/security-lighting-installation',
    element: <SecurityLightingInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/motion-sensor-lighting',
    element: <MotionSensorLightingPage />,
  },
  {
    path: '/services/lighting-outdoor/landscape-lighting-installation',
    element: <LandscapeLightingInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/garden-pathway-lighting',
    element: <GardenPathwayLightingPage />,
  },
  {
    path: '/services/lighting-outdoor/outdoor-power-outlet-installation',
    element: <OutdoorPowerOutletInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/patio-deck-lighting',
    element: <PatioDeckLightingPage />,
  },
  {
    path: '/services/lighting-outdoor/parking-lot-lighting-installation',
    element: <ParkingLotLightingInstallationPage />,
  },
  {
    path: '/services/lighting-outdoor/floodlight-installation',
    element: <FloodlightInstallationPage />,
  },
  // Commercial Specialist
  {
    path: '/services/commercial-specialist',
    element: <CommercialSpecialistPage />,
  },
  {
    path: '/services/commercial-specialist/commercial-electrical-installations',
    element: <CommercialElectricalInstallationsPage />,
  },
  {
    path: '/services/commercial-specialist/commercial-electrical-maintenance',
    element: <CommercialElectricalMaintenancePage />,
  },
  {
    path: '/services/commercial-specialist/office-electrical-fit-outs',
    element: <OfficeElectricalFitOutsPage />,
  },
  {
    path: '/services/commercial-specialist/retail-store-electrical',
    element: <RetailStoreElectricalPage />,
  },
  {
    path: '/services/commercial-specialist/restaurant-electrical-systems',
    element: <RestaurantElectricalSystemsPage />,
  },
  {
    path: '/services/commercial-specialist/warehouse-electrical',
    element: <WarehouseElectricalPage />,
  },
  {
    path: '/services/commercial-specialist/three-phase-power',
    element: <ThreePhasePowerPage />,
  },
  {
    path: '/services/commercial-specialist/commercial-lighting-systems',
    element: <CommercialLightingSystemsPage />,
  },
  {
    path: '/services/commercial-specialist/emergency-lighting',
    element: <EmergencyLightingPage />,
  },
  {
    path: '/services/commercial-specialist/commercial-panel-upgrades',
    element: <CommercialPanelUpgradesPage />,
  },
  {
    path: '/services/commercial-specialist/new-commercial-builds',
    element: <NewCommercialBuildsPage />,
  },
  {
    path: '/services/commercial-specialist/property-developers',
    element: <PropertyDevelopersPage />,
  },
  // Smart Home & Energy
  {
    path: '/services/smart-home-energy',
    element: <SmartHomeEnergyPage />,
  },
  {
    path: '/services/smart-home-energy/smart-lighting-installation',
    element: <SmartLightingInstallationPage />,
  },
  {
    path: '/services/smart-home-energy/smart-switch-installation',
    element: <SmartSwitchInstallationPage />,
  },
  {
    path: '/services/smart-home-energy/smart-thermostat-installation',
    element: <SmartThermostatInstallationPage />,
  },
  {
    path: '/services/smart-home-energy/home-automation-systems',
    element: <HomeAutomationSystemsPage />,
  },
  {
    path: '/services/smart-home-energy/smart-security-systems',
    element: <SmartSecuritySystemsPage />,
  },
  {
    path: '/services/smart-home-energy/energy-monitoring-systems',
    element: <EnergyMonitoringSystemsPage />,
  },
  {
    path: '/services/smart-home-energy/whole-home-energy-management',
    element: <WholeHomeEnergyManagementPage />,
  },
  {
    path: '/services/smart-home-energy/smart-doorbell-installation',
    element: <SmartDoorbellInstallationPage />,
  },
  {
    path: '/services/smart-home-energy/smart-appliance-integration',
    element: <SmartApplianceIntegrationPage />,
  },
  {
    path: '/services/smart-home-energy/smart-home-setup-configuration',
    element: <SmartHomeSetupConfigurationPage />,
  },
  // Renewables & EV
  {
    path: '/services/renewables-ev',
    element: <RenewablesEVPage />,
  },
  {
    path: '/services/renewables-ev/ev-charger-installation',
    element: <EVChargerInstallationPage />,
  },
  {
    path: '/services/renewables-ev/tesla-charger-installation',
    element: <TeslaChargerInstallationPage />,
  },
  {
    path: '/services/renewables-ev/home-ev-charging-stations',
    element: <HomeEVChargingStationsPage />,
  },
  {
    path: '/services/renewables-ev/commercial-ev-charger-installations',
    element: <CommercialEVChargerInstallationsPage />,
  },
  {
    path: '/services/renewables-ev/solar-panel-electrical-connections',
    element: <SolarPanelElectricalConnectionsPage />,
  },
  {
    path: '/services/renewables-ev/solar-inverter-installation',
    element: <SolarInverterInstallationPage />,
  },
  {
    path: '/services/renewables-ev/solar-battery-storage-installation',
    element: <SolarBatteryStorageInstallationPage />,
  },
  {
    path: '/services/renewables-ev/backup-battery-systems',
    element: <BackupBatterySystemsPage />,
  },
  {
    path: '/services/renewables-ev/electrical-systems-solar-installations',
    element: <ElectricalSystemsSolarInstallationsPage />,
  },
  {
    path: '/services/renewables-ev/solar-system-monitoring-setup',
    element: <SolarSystemMonitoringSetupPage />,
  },
  // Fire Safety
  {
    path: '/services/fire-safety',
    element: <FireSafetyPage />,
  },
  {
    path: '/services/fire-safety/fire-alarm-installation',
    element: <FireAlarmInstallationPage />,
  },
  {
    path: '/services/fire-safety/fire-alarm-maintenance',
    element: <FireAlarmMaintenancePage />,
  },
  {
    path: '/services/fire-safety/smoke-detector-systems',
    element: <SmokeDetectorSystemsPage />,
  },
  {
    path: '/services/fire-safety/carbon-monoxide-detection',
    element: <CarbonMonoxideDetectionPage />,
  },
  {
    path: '/services/fire-safety/emergency-lighting-systems',
    element: <EmergencyLightingSystemsPage />,
  },
  {
    path: '/services/fire-safety/fire-alarm-panel-installation',
    element: <FireAlarmPanelInstallationPage />,
  },
  {
    path: '/services/fire-safety/fire-alarm-testing-certification',
    element: <FireAlarmTestingCertificationPage />,
  },
  {
    path: '/services/fire-safety/fire-alarm-upgrades',
    element: <FireAlarmUpgradesPage />,
  },
  {
    path: '/services/fire-safety/fire-alarm-monitoring',
    element: <FireAlarmMonitoringPage />,
  },
  {
    path: '/services/fire-safety/fire-safety-compliance',
    element: <FireSafetyCompliancePage />,
  },
  // Area Pages
  {
    path: '/areas/fresno',
    element: <FresnoAreaRedirect />,
  },
  {
    path: '/areas/clovis',
    element: <ClovisPage />,
  },
  {
    path: '/areas/madera',
    element: <MaderaPage />,
  },
  {
    path: '/areas/visalia',
    element: <VisaliaPage />,
  },
  {
    path: '/areas/hanford',
    element: <HanfordPage />,
  },
  {
    path: '/areas/tulare',
    element: <TularePage />,
  },
  {
    path: '/areas/selma',
    element: <SelmaPage />,
  },
  {
    path: '/areas/sanger',
    element: <SangerPage />,
  },
  {
    path: '/areas/reedley',
    element: <ReedleyPage />,
  },
  {
    path: '/areas/dinuba',
    element: <DinubaPage />,
  },
  {
    path: '/areas/porterville',
    element: <PortervillePage />,
  },
  {
    path: '/areas/fowler',
    element: <FowlerPage />,
  },
  // Blog
  {
    path: '/blog',
    element: <BlogPage />,
  },
  {
    path: '/blog/when-to-upgrade-electrical-panel-fresno',
    element: <PanelUpgradeBlogPost />,
  },
  {
    path: '/blog/ev-charger-installation-fresno-homeowners',
    element: <EVChargerBlogPost />,
  },
  {
    path: '/blog/fresno-home-electrical-safety-checklist',
    element: <SafetyChecklistBlogPost />,
  },
  {
    path: '/blog/landlord-electrical-safety-checks-fresno',
    element: <LandlordBlogPost />,
  },
  {
    path: '/blog/smart-home-upgrades-fresno-summer',
    element: <SmartHomeBlogPost />,
  },
  {
    path: '/blog/solar-electrical-connections-fresno',
    element: <SolarBlogPost />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;